import {
  Box,
  DatePicker,
  DatePickerValue,
  FieldSwitch,
  FieldText,
  FlexBox,
  Icon,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import { ampli } from 'lib-shared-tracking';
import type { OpeningHours } from 'lib-shared-types-api';
import moment, { Moment } from 'moment-timezone';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import {
  getLocalDate,
  isFebruary29,
} from 'components/settings/components/sections/openingHours/utils/date';
import type { Errors } from 'components/settings/components/sections/openingHours/views/schedule/schedule.context';
import { getFormattedDate } from 'components/settings/components/sections/openingHours/views/schedule/utils/format';
import { validateIntervals } from 'components/settings/components/sections/openingHours/views/schedule/utils/validate';
import { defaultTime } from 'utils/date';
import { getUserDisplayName, useCurrentUser } from 'utils/user';

import { IntervalsSelect } from '../../../intervalsSelect/intervalsSelect';

import styles from './addExceptionModal.module.scss';

type Props = {
  exceptions: OpeningHours[];
  onModalClose: VoidFunction;
  onSubmit: (exception: OpeningHours) => void;
};

function getDateAsUTC(date: Moment): Moment {
  return moment.utc({
    year: date.year(),
    month: date.month(),
    date: date.date(),
  });
}

export const AddExceptionModal = ({
  exceptions,
  onModalClose,
  onSubmit,
}: Props): JSX.Element => {
  const [date, setDate] = useState<null | Moment>(null);
  const [isOpen, setIsOpen] = useState(true);
  const [intervals, setIntervals] = useState<OpeningHours['intervals']>([
    defaultTime,
  ]);
  const [isRecurring, setIsRecurring] = useState(false);
  const [name, setName] = useState<string>('');
  const [errors, setErrors] = useState<Errors>([]);

  const { user } = useCurrentUser();
  const agentName = getUserDisplayName(user);

  const handleIsOutsideRange = useCallback(
    (day: Moment) => {
      const isReservedDate = exceptions.find(({ next_date }) =>
        moment(next_date).isSame(day, 'day'),
      );
      const isBeforeToday = day.isBefore(getLocalDate(), 'day');
      const isMoreThanAYear = day.isAfter(
        getLocalDate().add(1, 'year').subtract(1, 'day'),
        'day',
      );

      return isBeforeToday || isMoreThanAYear || isReservedDate;
    },
    [exceptions],
  );

  const handleValidateInterval = useCallback(
    (intervals) =>
      void setErrors((stateErrors) =>
        validateIntervals({
          intervals,
          dayIndex: 0,
          errors: stateErrors,
        }),
      ),
    [],
  );

  const handleDateChange = useCallback(
    ({ localDate }: DatePickerValue) => void setDate(localDate as Moment),
    [],
  );

  const handleIsOpenChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      void setIsOpen(event?.target.checked),
    [],
  );

  const handleIsRecurringChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      void setIsRecurring(event?.target.checked),
    [],
  );

  const handleNameChange = useCallback(
    (event?: ChangeEvent<HTMLInputElement>) =>
      void setName(event?.target?.value ?? ''),
    [],
  );

  const hasDateAndName = !!date && !!name;
  const hasNoErrors = !errors[0]?.some((e) => !!e);

  const isValid = useMemo(
    () => !!(hasDateAndName && (!isOpen || hasNoErrors)),
    [hasDateAndName, hasNoErrors, isOpen],
  );

  const handleSubmit = useCallback(() => {
    ampli.businessHoursScheduleAddExceptionFormSubmitted();

    if (date) {
      const startDateAsUTC = getDateAsUTC(date);

      onSubmit({
        created_at: moment().toISOString(),
        created_by: agentName,
        duration: 1,
        id: uuid(),
        intervals: isOpen ? intervals : [],
        name,
        next_date: startDateAsUTC.toISOString(),
        opening_hour_type: 'exception',
        recurring: isRecurring
          ? {
              day_of_month: parseInt(startDateAsUTC.format('D')) as number,
              month_of_year: parseInt(startDateAsUTC.format('M')) as number,
            }
          : null,
        start_date: startDateAsUTC.toISOString(),
      });

      onModalClose();
    }
  }, [
    agentName,
    date,
    intervals,
    isRecurring,
    isOpen,
    name,
    onModalClose,
    onSubmit,
  ]);

  const handleModalClose = useCallback(() => {
    ampli.businessHoursScheduleAddExceptionFormCancelled();
    onModalClose();
  }, [onModalClose]);

  return (
    <Modal className={styles.modal} isOpen onClose={handleModalClose}>
      <ModalHead onClose={handleModalClose} title="Add exception"></ModalHead>
      <ModalBody className={styles.modalBody}>
        {/* To prevent DatePicker component to re-render and switch back to the
        initial view (current month), when changing state values. */}
        {useMemo(
          () => (
            <DatePicker
              calendarProps={
                {
                  daySize: 50,
                  isOutsideRange: handleIsOutsideRange,
                } as unknown as undefined
              }
              minDate={moment().add(-1, 'days')}
              date={date}
              onChange={handleDateChange}
            />
          ),
          [date, handleDateChange, handleIsOutsideRange],
        )}
        {!!date && (
          <Box className={styles.details}>
            <FlexBox flexDirection="column" margin="0 0 400" gap="250">
              <Box className={styles.intervalsBox}>
                <div className={styles.toggleBox}>
                  <Text.Paragraph variant="1">
                    {getFormattedDate(getDateAsUTC(date).toISOString())}
                  </Text.Paragraph>
                  <FieldSwitch
                    className={styles.fieldSwitch}
                    input={{
                      name: 'is-open',
                      value: '',
                      checked: isOpen,
                      onChange: handleIsOpenChange,
                    }}
                  />
                </div>
                {isOpen && (
                  <IntervalsSelect
                    onChange={setIntervals}
                    isClosed={false}
                    intervals={intervals}
                    errors={errors[0] || []}
                    validate={handleValidateInterval}
                  />
                )}
              </Box>
              <Box className={styles.recurringSettings}>
                <FlexBox justifyContent="space-between" alignItems="center">
                  <Text.Paragraph variant="1">
                    Repeat annually on this date
                  </Text.Paragraph>
                  <FieldSwitch
                    className={styles.fieldSwitch}
                    input={{
                      name: 'is-recurring',
                      value: '',
                      checked: isRecurring,
                      onChange: handleIsRecurringChange,
                    }}
                  />
                </FlexBox>
              </Box>
              {date && isFebruary29(date) && (
                <FlexBox gap="200">
                  <Icon
                    name="exclamation-circle-filled"
                    color={typography.color.error}
                  />
                  <Text.Paragraph variant="4" color={typography.color.error}>
                    This exception repeats every 4 years on 29 February.
                  </Text.Paragraph>
                </FlexBox>
              )}
            </FlexBox>
            <FieldText
              input={{
                name: 'exception-name',
                value: name,
                onChange: handleNameChange,
              }}
              label="Name"
              noAlert
              placeholder="E.g. special sale, offsite, partial day closure, etc."
              required
            />
          </Box>
        )}
      </ModalBody>
      <ModalFoot
        cancelOnClick={handleModalClose}
        confirmName="Add"
        confirmOnClick={handleSubmit}
        confirmProps={{ disabled: !isValid }}
      />
    </Modal>
  );
};
